import {
  ellipsoidalHeightFromCoordinates,
  formatElevationFeet,
  formatElevationValueWithoutGeoid,
  formatReferenceFrameValue,
} from '@civilgrid/utils/src/geoItems/elevationDisclosure';
import { formatAccuracyFeet } from '@civilgrid/utils/src/geoItems/unitConversion';
import { getSelectedUtilities, resolveLegacyFieldValue } from '@civilgrid/utils/src/geoItems/utilityMeasurements';
import type { GeoItem } from '~/common/types';

function getFieldValue(item: GeoItem, field: string): string {
  const value = item.fields?.[field]?.value;
  if (typeof value === 'string') {
    return value;
  }
  if (Array.isArray(value)) {
    // Drop empty-string entries — a stray blank selection would otherwise produce a
    // leading/trailing blank line when joined.
    return value.filter((v): v is string => typeof v === 'string' && v.length > 0).join('\n');
  }
  return '';
}

export type PotholeCsvField = {
  header: string;
  getValue: (geoItem: GeoItem) => string;
};

// General pothole fields, record metadata, and accuracy fields. Bare, unresolved IDs
// (Geo Item ID, Updated By ID) are deliberately excluded as not human-readable;
// `Created By` is a resolved name, not an ID, so it's kept.
const GENERAL_FIELDS: readonly PotholeCsvField[] = [
  { header: 'PH#', getValue: (g) => getFieldValue(g, 'PH#') },
  { header: 'Status', getValue: (g) => g.status ?? '' },
  { header: 'Surface Type', getValue: (g) => getFieldValue(g, 'Surface Type') },
  { header: 'Surface Thickness (in.)', getValue: (g) => getFieldValue(g, 'Surface Thickness') },
  { header: 'Base Thickness (in.)', getValue: (g) => getFieldValue(g, 'Base Thickness') },
  { header: 'Hole Depth (in.)', getValue: (g) => getFieldValue(g, 'Hole Depth') },
  { header: 'Soil', getValue: (g) => getFieldValue(g, 'Soil') },
  { header: 'Priority', getValue: (g) => getFieldValue(g, 'Priority') },
  { header: 'Location', getValue: (g) => getFieldValue(g, 'Location') },
  { header: 'Address', getValue: (g) => getFieldValue(g, 'Address') },
  { header: 'Created By', getValue: (g) => g.createdBy?.name ?? '' },
  { header: 'Updated At', getValue: (g) => (g.updatedAt ? g.updatedAt.toLocaleString() : '') },
  // Raw coordinate, not the table's `.toFixed(6)` display formatting.
  { header: 'Lat', getValue: (g) => String(g.geometry.coordinates[1] ?? '') },
  { header: 'Lng', getValue: (g) => String(g.geometry.coordinates[0] ?? '') },
  // The orthometric height from `accuracy.mslHeight`, qualified with the datum because a plain
  // `Elevation` header would collide with the legacy user-typed column below.
  {
    header: 'Elevation (NAVD88, ft)',
    getValue: (g) => formatElevationValueWithoutGeoid(g.accuracy),
  },
  { header: 'Geoid Model', getValue: (g) => g.accuracy?.geoidModel ?? '' },
  { header: 'Reference Frame', getValue: (g) => formatReferenceFrameValue(g.accuracy) },
  // Unlike the PDF, this column is always present rather than a per-row fallback
  {
    header: 'Ellipsoidal Height (ft)',
    getValue: (g) => {
      const ellipsoidal = ellipsoidalHeightFromCoordinates(g.geometry.coordinates);
      return ellipsoidal != null ? formatElevationFeet(ellipsoidal) : '';
    },
  },
  // Source values are stored in meters; shown in feet with the unit spelled out.
  {
    header: 'HRMS (ft)',
    getValue: (g) => (g.accuracy?.hrms != null ? `${formatAccuracyFeet(g.accuracy.hrms)} ft` : ''),
  },
  {
    header: 'VRMS (ft)',
    getValue: (g) => (g.accuracy?.vrms != null ? `${formatAccuracyFeet(g.accuracy.vrms)} ft` : ''),
  },
  {
    // 'Unknown' (not blank) when absent, matching the PDF detail page and KMZ export.
    header: 'Antenna Height (ft)',
    getValue: (g) =>
      g.accuracy?.antennaHeight != null ? `${formatAccuracyFeet(g.accuracy.antennaHeight)} ft` : 'Unknown',
  },
  { header: 'Solution Type', getValue: (g) => g.accuracy?.solutionType ?? '' },
  { header: 'Receiver Make', getValue: (g) => g.accuracy?.receiverMake ?? '' },
  { header: 'Receiver Model', getValue: (g) => g.accuracy?.receiverModel ?? '' },
  { header: 'Recorded At (UTC)', getValue: (g) => g.accuracy?.recordedAt ?? '' },
];

// Per-utility fields, one utility per line via `\n`. `Depth` (the generic legacy
// field, distinct from Depth to Top/Bottom) is unsurfaced elsewhere but included
// here since this export must carry everything.
const PER_UTILITY_FIELDS: readonly PotholeCsvField[] = [
  { header: 'Utility', getValue: (g) => getSelectedUtilities(g.props).join('\n') },
  { header: 'Target Utility', getValue: (g) => getFieldValue(g, 'Target Utility') },
  { header: 'Utility Owner', getValue: (g) => resolveLegacyFieldValue(g.props, 'Utility Owner', undefined, '\n') },
  {
    header: 'Depth to Top (in.)',
    getValue: (g) => resolveLegacyFieldValue(g.props, 'Depth to Top', undefined, '\n'),
  },
  {
    header: 'Depth to Bottom (in.)',
    getValue: (g) => resolveLegacyFieldValue(g.props, 'Depth to Bottom', undefined, '\n'),
  },
  { header: 'Depth', getValue: (g) => resolveLegacyFieldValue(g.props, 'Depth', undefined, '\n') },
  { header: 'Size (in.)', getValue: (g) => resolveLegacyFieldValue(g.props, 'Size', undefined, '\n') },
  { header: 'Material', getValue: (g) => resolveLegacyFieldValue(g.props, 'Material', undefined, '\n') },
];

const DATE_FIELD: PotholeCsvField = { header: 'Date', getValue: (g) => g.createdAt.toLocaleString() };

/**
 * Legacy Survey Results fields being phased out; only appear as columns when a
 * record in the batch has data, so exports aren't cluttered with empty columns.
 * Falls back to the deprecated `Longitude/Latitude` (slash) label for older records.
 */
const CONDITIONAL_LEGACY_SURVEY_FIELDS: readonly PotholeCsvField[] = [
  {
    header: 'Longitude, Latitude',
    getValue: (g) => getFieldValue(g, 'Longitude, Latitude') || getFieldValue(g, 'Longitude/Latitude'),
  },
  { header: 'Survey Date', getValue: (g) => getFieldValue(g, 'Survey Date') },
  { header: 'Surveyed By', getValue: (g) => getFieldValue(g, 'Surveyed By') },
  { header: 'Northing', getValue: (g) => getFieldValue(g, 'Northing') },
  { header: 'Easting', getValue: (g) => getFieldValue(g, 'Easting') },
  { header: 'Elevation', getValue: (g) => getFieldValue(g, 'Elevation') },
];

// Last: this is a free-text field that can run long, and CSV has no cell-level
// "wrap text" styling — keeping it last avoids it overflowing across other columns.
const NOTES_FIELD: PotholeCsvField = { header: 'Notes', getValue: (g) => getFieldValue(g, 'Notes') };

/**
 * Builds the pothole CSV export's field list for a specific batch of records.
 * Independent of `potholeColumns`/the on-screen table by design — CSV export must
 * not affect what's displayed on screen, its column order, or its column set.
 *
 * Order: general fields, then all per-utility fields grouped together, then Date,
 * then any legacy Survey Results fields that actually have data in this batch, then
 * Notes last. Unlike the PDF summary table, nothing here is truncated.
 */
export function buildPotholeCsvExportFields(geoItems: readonly GeoItem[]): PotholeCsvField[] {
  const conditionalFields = CONDITIONAL_LEGACY_SURVEY_FIELDS.filter((field) =>
    geoItems.some((g) => field.getValue(g).trim() !== ''),
  );
  return [...GENERAL_FIELDS, ...PER_UTILITY_FIELDS, DATE_FIELD, ...conditionalFields, NOTES_FIELD];
}
